import { useState } from 'react'
import { toast } from 'react-toastify'

import { getTableTransformers } from '@/containers/onlineClients/clientTable/CustomerColumnTransform'
import { Customer, CustomerCategory } from '@/interfaces/billing'

import {
  codeGenerator,
  procesarPropiedades,
} from '../containers/analysis/SaleByOrdersTabs/Export'
import { BasicType } from '../interfaces/InterfacesLocal'
import {
  AddressInterface,
  OnlineClientInterface,
  OrderInterface,
  PaginateInterface,
} from '@/interfaces/ServerInterfaces'
import query from './APIServices'
import useServer from './useServerMain'
import { generateUrlParams, exportExcel } from '../utils/helpers'

const useServerOnlineClients = () => {
  const { manageErrors } = useServer()

  const [isGetClient, setGetClient] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [paginate, setPaginate] = useState<PaginateInterface>({
    currentPage: 0,
    totalItems: 0,
    totalPages: 0,
  })
  const [paginateCategories, setPaginateCategories] =
    useState<PaginateInterface>({
      currentPage: 0,
      totalItems: 0,
      totalPages: 0,
    })
  const [paginateAssOrders, setPaginateAssOrders] = useState<PaginateInterface>(
    {
      currentPage: 0,
      totalItems: 0,
      totalPages: 0,
    }
  )
  const [allClients, setAllClients] = useState<OnlineClientInterface[]>([])
  const [allAddress, setAllAddress] = useState<AddressInterface[]>([])
  const [allCategories, setAllCategories] = useState<CustomerCategory[]>([])
  const [client, setClient] = useState<OnlineClientInterface | null>(null)
  const [clientOrders] = useState<OrderInterface[]>([])
  const helperArray: number[] = []

  const getAllClients = async (filter?: BasicType) => {
    setIsLoading(true)
    await query
      .get(`/customer/${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllClients(resp.data.items)
        setPaginate({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }
  const getAllAddress = async (idClient: number, filter?: BasicType) => {
    setIsLoading(true)
    await query
      .get(`/customer/address/${idClient}${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllAddress(resp.data)
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const addAddress = async (
    idClient: number,
    data: Record<string, string | number | boolean>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .post(`/customer/address/${idClient}`, data)
      .then((resp) => {
        setAllAddress([resp.data, ...allAddress])
        callback()
        toast.success('Dirección agregada correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const editAddress = async (
    id: number,
    data: Record<string, string | number | boolean>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/customer/address/${id}`, data)
      .then((resp) => {
        const newAddress = allAddress.map((element: AddressInterface) => {
          if (element.id === resp.data?.id) {
            return resp.data
          } else {
            return element
          }
        })
        setAllAddress(newAddress)
        toast.success('Dirección actualizada')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const deleteAddress = async (id: number, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/customer/address/${id}`, {})
      .then(() => {
        setAllAddress(allAddress.filter((item) => item.id !== id))
        toast.success('Dirección eliminada con éxito')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const getAllCustomerCategories = async (filter?: BasicType) => {
    setIsLoading(true)
    await query
      .get(`/customer/v2/categories/${generateUrlParams(filter)}`)
      .then((resp) => {
        setAllCategories(resp.data.items)
        setPaginateCategories({
          currentPage: resp.data.currentPage,
          totalItems: resp.data.totalItems,
          totalPages: resp.data.totalPages,
        })
      })
      .catch((e) => manageErrors(e))
    setIsLoading(false)
  }

  const addClient = async (
    data: Record<string, string | number | boolean>,
    callback: (_data: OnlineClientInterface) => void
  ) => {
    setIsFetching(true)
    await query
      .post('/customer', data)
      .then((resp) => {
        setAllClients([resp.data, ...allClients])
        setPaginate({
          ...paginate,
          totalItems: (paginate?.totalItems ?? 0) + 1,
        })
        helperArray.push(resp.data.id)
        callback(resp.data)
        toast.success('Cliente agregado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const addCustomerCategory = async (
    data: Record<string, string | number | boolean>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .post('/customer/categories', data)
      .then((resp) => {
        setAllCategories([resp.data, ...allCategories])
        setPaginateCategories({
          ...paginateCategories,
          totalItems: (paginateCategories?.totalItems ?? 0) + 1,
        })
        callback()
        toast.success('Categoría agregado correctamente')
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const editClient = async (
    id: number,
    data: Record<string, string | number | boolean>,
    callBack?: (_data: OnlineClientInterface) => void
  ) => {
    setIsEdit(true)
    setIsLoading(true)
    await query
      .patch(`/customer/${id}`, data)
      .then((resp) => {
        setClient(resp.data)
        const newAllClients = allClients.map((element) => {
          if (element.id === client?.id) {
            return resp.data
          } else {
            return element
          }
        })
        setAllClients(newAllClients)
        setClient(resp.data)
        if (callBack) callBack(resp.data)
        toast.success('Datos actualizados')
      })
      .catch((e) => manageErrors(e))
    setIsEdit(false)
    setIsLoading(false)
  }
  const editCategoryClient = async (
    id: number,
    data: Record<string, string | number | boolean>,
    callback: () => void
  ) => {
    setIsFetching(true)
    await query
      .patch(`/customer/categories/${id}`, data)
      .then((resp) => {
        const newCategoryClient = allCategories.map(
          (element: CustomerCategory) => {
            if (element.id === resp.data?.id) {
              return resp.data
            } else {
              return element
            }
          }
        )
        setAllCategories(newCategoryClient)
        toast.success('Datos actualizados')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const deleteClient = async (id: number, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/customer/${id}`, {})
      .then(() => {
        setAllClients(allClients.filter((item) => item.id !== id))
        toast.success('Cliente eliminado con éxito')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  const deleteClientCategory = async (id: number, callback: () => void) => {
    setIsFetching(true)
    await query
      .deleteAPI(`/customer/categories/${id}`, {})
      .then(() => {
        setAllCategories(
          allCategories.filter((item: CustomerCategory) => item.id !== id)
        )
        setPaginateCategories({
          ...paginateCategories,
          totalItems: paginateCategories?.totalItems - 1,
        })
        toast.success('Categoría eliminado con éxito')
        callback()
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }
  interface ApiErrorResponse {
    message?: string
    error?: string
    errors?: Record<string, string[]>
  }

  interface ApiError {
    status?: number
    response?: {
      data?: ApiErrorResponse
    }
    message?: string
  }

  const mergeClients = async (payload: {
    rootId: number
    ids: number[]
    rootData?: BasicType
  }) => {
    setIsFetching(true)
    try {
      const resp = await query.post('/customer/merge', payload)
      return resp.data
    } catch (e) {
      const manageErrorsSafe = (err: unknown) => {
        try {
          manageErrors(err as ApiError)
        } catch {
          toast.error(
            'No fue posible unificar los clientes. Intente nuevamente.'
          )
        }
      }
      manageErrorsSafe(e)
      throw e
    } finally {
      setIsFetching(false)
    }
  }

  const getClient = async (
    clientId: string | number,
    callback?: (_data: OnlineClientInterface) => void
  ) => {
    setGetClient(true)
    await query
      .get(`/customer/${clientId}`)
      .then((resp) => {
        setClient(resp.data)
        if (callback) {
          callback(resp.data)
        }
      })
      .catch((e) => manageErrors(e))
    setGetClient(false)
  }

  const getMainClientInfo = async (
    clientId: string | number,
    filter: BasicType,
    setOrderState: (_data: OrderInterface[]) => void
  ) => {
    const queries = [
      query.get(`/sales/order${generateUrlParams({ clientId, ...filter })}`),
    ]
    if (!client) queries.push(query.get(`/customer/${clientId}`))
    setIsFetching(true)
    await Promise.all(queries)
      .then((resp) => {
        setOrderState(resp[0].data.items)
        setPaginateAssOrders({
          totalItems: resp[0].data.totalItems,
          totalPages: resp[0].data.totalPages,
          currentPage: resp[0].data.currentPage,
        })
        if (!client) setClient(resp[1].data)
      })
      .catch((e) => manageErrors(e))
    setIsFetching(false)
  }

  const exportClients = async (
    filename: string,
    customers: Customer[],
    columnsHeadersToExport: string[],
    callback?: () => void
  ) => {
    setIsLoading(true)

    let dataToExport: Record<string, string | number>[] = []

    customers.forEach((item) => {
      const row: Record<string, string | number> = {}

      columnsHeadersToExport.forEach((header) => {
        row[header] = ''
        const transformer = getTableTransformers(item)[header]
        if (transformer) row[header] = transformer()
      })

      dataToExport.push(row)
    })

    let dataForExcel: Record<string, string | number>[] = []
    let codeGeneratorValues: Record<string, string | number>[] = []

    if (dataToExport && Array.isArray(dataToExport) && columnsHeadersToExport) {
      // Keep only selected headers in export rows
      const filtrarPropiedades = (
        rows: Record<string, string | number>[],
        headers: string[]
      ) => {
        return rows.map((r) => {
          const o: Record<string, string | number> = {}
          headers.forEach((h) => {
            o[h] = r[h] ?? ''
          })
          return o
        })
      }
      dataToExport = filtrarPropiedades(dataToExport, columnsHeadersToExport)
    }

    if (dataToExport && Array.isArray(dataToExport) && columnsHeadersToExport) {
      dataForExcel = procesarPropiedades(dataToExport)
      codeGeneratorValues = codeGenerator(dataForExcel)
    }

    const data: Record<string, string | number>[] = codeGeneratorValues ?? []

    exportExcel(data, filename)
    if (callback) callback()

    setIsLoading(false)
  }

  return {
    isLoading,
    isFetching,
    isEdit,
    isGetClient,
    allClients,
    paginate,
    client,
    clientOrders,
    getMainClientInfo,
    getAllClients,
    getClient,
    addClient,
    editClient,
    deleteClient,
    exportClients,
    helperArray,
    getAllCustomerCategories,
    allCategories,
    paginateCategories,
    paginateAssOrders,
    addCustomerCategory,
    editCategoryClient,
    deleteClientCategory,
    getAllAddress,
    allAddress,
    addAddress,
    editAddress,
    deleteAddress,
    mergeClients,
  }
}

export default useServerOnlineClients
